import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, Tooltip, Icon,
} from 'antd';
import translate from 'app/translations';
import FormControlRules from './FormControlRules';
import FormUtils from './FormUtils';
import './FormControl.scss';

const FormItem = Form.Item;
const { TextArea } = Input;

class FormControl extends Component {
  constructor() {
    super();
    this.t = translate;
    this.getDisplayName = this.getDisplayName.bind(this);
    this.getClassName = this.getClassName.bind(this);
    this.getRules = this.getRules.bind(this);
    this.getValidatorRequired = this.getValidatorRequired.bind(this);
  }

  getClassName() {
    const classList = [];

    if (!this.props.hideLabel) {
      classList.push('form-control');
    }

    if (this.props.className) {
      classList.push(this.props.className);
    }

    return classList.join(' ');
  }

  getDisplayName() {
    return this.props.displayName || this.props.fieldName;
  }

  getRules() {
    const ruleList = [];
    let setDefaultLength = true;
    if (this.props.rules) {
      this.props.rules.forEach((rule) => {
        const ruleName = (typeof rule === 'object') ? Object.keys(rule)[0] : rule;
        switch (ruleName) {
          case FormControlRules.REQUIRED: {
            ruleList.push(this.getValidatorRequired());
            break;
          }
          case FormControlRules.URL: {
            ruleList.push(this.getValidatorUrl());
            break;
          }
          case FormControlRules.PATTERN: {
            ruleList.push(this.getValidatorPattern(rule[ruleName]));
            break;
          }
          case FormControlRules.EMAIL: {
            ruleList.push(this.getValidatorEmail());
            break;
          }
          case FormControlRules.ARRAY: {
            ruleList.push({ type: 'array' });
            break;
          }
          case FormControlRules.LENGTH: {
            ruleList.push(this.getValidatorLength(rule[ruleName]));
            break;
          }
          case FormControlRules.EMAILMATCH: {
            ruleList.push(this.getValidatorEmailMatch(rule[ruleName]));
            break;
          }
          case FormControlRules.MAXNUMERIC: {
            ruleList.push(this.getValidatorMaxNumericValue(rule[ruleName]));
            break;
          }
          case FormControlRules.POSITIVEINT: {
            ruleList.push(this.getValidatorPositiveInt());
            break;
          }
          case FormControlRules.MAXLENGTH: {
            setDefaultLength = false;
            ruleList.push(this.getValidatorMaxLength(rule[ruleName]));
            break;
          }
          case FormControlRules.CURRENCY: {
            ruleList.push(this.getValidatorCurrency());
            break;
          }
          case FormControlRules.FUNCTION: {
            ruleList.push(rule[ruleName]);
            break;
          }
          default:
            break;
        }
      });
    }

    if (setDefaultLength && (this.isInput() || this.isTextArea())
      && this.props.children.props.type !== 'number'
    ) {
      const maxLength = this.isTextArea()
        ? this.props.maxLength.textArea
        : this.props.maxLength.input;
      ruleList.push(this.getValidatorMaxLength(maxLength));
    }

    return ruleList;
  }

  getValidatorRequired() {
    const displayNameUsed = this.getDisplayName();
    return {
      required: true,
      message: this.t(
        'FormControl.validate.required',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorUrl() {
    const displayNameUsed = this.getDisplayName();
    return {
      type: 'url',
      message: this.t(
        'FormControl.validate.url',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorPattern(regex) {
    const displayNameUsed = this.getDisplayName();
    return {
      pattern: regex,
      message: this.t(
        'FormControl.validate.pattern',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorLength(argList = []) {
    const displayNameUsed = this.getDisplayName();
    const [length, msgLength] = argList;
    return {
      message: this.t(
        'FormControl.validate.length',
        { fieldName: displayNameUsed, length: msgLength },
      ),
      validator: (_, value, callback) => {
        const fieldLength = `${value}`.trim().length;
        if (fieldLength > 0 && fieldLength !== length) {
          callback(true);
        } else {
          callback();
        }
      },
    };
  }

  getValidatorMaxNumericValue(argList = []) {
    const displayNameUsed = this.getDisplayName();
    const [maxValue, prefix, suffix] = argList;
    return {
      message: this.t(
        'FormControl.validate.maxNumericValue',
        { fieldName: displayNameUsed, maxValue: `${prefix || ''}${maxValue}${suffix || ''}` },
      ),
      validator: (_, value, callback) => {
        const stringValue = `${value}`.trim();

        const numberVal = stringValue.match(/[^0-9]/) ? 0 : Number(stringValue);

        if (stringValue.length > 0 && numberVal > maxValue) {
          callback(true);
        } else {
          callback();
        }
      },
    };
  }

  getValidatorEmailMatch(paymentEmail) {
    const displayNameUsed = this.getDisplayName();
    return {
      message: this.t(
        'FormControl.validate.emailMatch',
        { fieldName: displayNameUsed },
      ),
      validator: (_, value, callback) => {
        const stringValue = `${value}`.trim();
        if (paymentEmail === undefined || paymentEmail.length === 0) {
          callback();
        } else if (paymentEmail === stringValue) {
          callback();
        } else {
          callback(true);
        }
      },
    };
  }

  getValidatorEmail() {
    const displayNameUsed = this.getDisplayName();
    return {
      type: 'email',
      message: this.t(
        'FormControl.validate.email',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorPositiveInt() {
    const displayNameUsed = this.getDisplayName();
    return {
      pattern: FormUtils.regex.positiveInt,
      message: this.t(
        'FormControl.validate.positiveInt',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorCurrency() {
    const displayNameUsed = this.getDisplayName();
    return {
      pattern: FormUtils.regex.currency,
      message: this.t(
        'FormControl.validate.currency',
        { fieldName: displayNameUsed },
      ),
    };
  }

  getValidatorMaxLength(maxLength) {
    const displayNameUsed = this.getDisplayName();
    return {
      max: maxLength,
      message: this.t(
        'FormControl.validate.maxLength',
        { fieldName: displayNameUsed, maxLength },
      ),
    };
  }

  autoTrimElement(element) {
    return React.cloneElement(element, {
      onBlur: (e) => {
        e.target.value = `${e.target.value}`.trim();
      },
    });
  }

  isInput() {
    if (this.props.children && this.props.children.type && this.props.children.type.prototype) {
      return (this.props.children.type.prototype.constructor === Input.prototype.constructor);
    }
    return false;
  }

  isTextArea() {
    if (this.props.children && this.props.children.type && this.props.children.type.prototype) {
      return (this.props.children.type.prototype.constructor === TextArea.prototype.constructor);
    }
    return false;
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const {
      fieldName, children, readOnly, readOnlyDisplayText, initialValue,
    } = this.props;
    const options = {
      initialValue: this.props.initialValue,
      validateTrigger: this.props.validateTrigger,
      rules: this.getRules(),
    };

    if (this.props.isCheckbox === 'checkbox') {
      options.valuePropName = 'checked';
    }

    let label = null;

    if (!this.props.hideLabel && this.props.toolTipText) {
      label = (
        <span>
          <span>{this.getDisplayName()} </span>
          <Tooltip title={this.props.toolTipText}>
            <Icon type="question-circle-o" />
          </Tooltip>
        </span>
      );
    } else if (!this.props.hideLabel) {
      label = this.getDisplayName();
    }

    return (
      <FormItem
        className={this.getClassName()}
        label={label}
      >
        {readOnly === 'readOnly' && (
          <div className="read-only">
            {readOnlyDisplayText || initialValue}
          </div>
        )}
        {readOnly !== 'readOnly' && (
          getFieldDecorator(
            fieldName,
            { ...options },
          )((this.isInput() || this.isTextArea())
            ? this.autoTrimElement(children)
            : children)
        )}
      </FormItem>
    );
  }
}

FormControl.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldsValue: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldError: PropTypes.func.isRequired,
    isFieldTouched: PropTypes.func.isRequired,
  }).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.arrayOf(PropTypes.element).isRequired,
  ]),
  className: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  isCheckbox: PropTypes.string,
  readOnly: PropTypes.string,
  readOnlyDisplayText: PropTypes.string,
  hideLabel: PropTypes.bool,
  validateTrigger: PropTypes.string,
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.instanceOf(Object),
    PropTypes.arrayOf(PropTypes.string),
  ]),
  rules: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
  maxLength: PropTypes.shape({
    input: PropTypes.number,
    textArea: PropTypes.number,
  }),
  toolTipText: PropTypes.string,
};

FormControl.defaultProps = {
  className: '',
  displayName: '',
  toolTipText: '',
  initialValue: undefined,
  rules: undefined,
  isCheckbox: '',
  readOnly: '',
  readOnlyDisplayText: '',
  children: undefined,
  hideLabel: false,
  validateTrigger: 'onBlur',
  maxLength: {
    input: 100,
    textArea: 1000,
  },
};

export default FormControl;
